import moment from "moment";

/**
 * Relative times ("in 4 days") in the widget language.
 *
 * moment only switches to a locale for an exact key it knows, and on an
 * unknown key it silently keeps the previous one. Gleap languages look like
 * `zh_TW`, `de-at` or `no`, so they are mapped explicitly. Languages moment
 * has no data for fall back to Intl.RelativeTimeFormat, then to English.
 * Mirrors Server/src/services/helper/relativeTime.ts.
 */

// Base codes whose moment locale lives under another key.
const MOMENT_ALIASES: Record<string, string> = {
  no: "nb",
  zh: "zh-cn",
};

const normalize = (lang?: string) =>
  (lang || "en").trim().toLowerCase().replace(/_/g, "-");

/** The moment locale for a Gleap language code, or null when moment has none. */
export const momentLocaleFor = (lang?: string): string | null => {
  const locales = new Set(moment.locales());
  const code = normalize(lang);
  if (locales.has(code)) {
    return code;
  }
  const base = code.split("-")[0];
  const alias = MOMENT_ALIASES[base];
  if (alias && locales.has(alias)) {
    return alias;
  }
  if (locales.has(base)) {
    return base;
  }
  // Languages moment only ships regionally: hy -> hy-am, tl -> tl-ph.
  for (const locale of Array.from(locales)) {
    if (locale.startsWith(`${base}-`)) {
      return locale;
    }
  }
  return null;
};

// moment's own humanize thresholds, so both paths round the same way.
const INTL_STEPS: Array<
  [moment.unitOfTime.Base, Intl.RelativeTimeFormatUnit, number]
> = [
  ["seconds", "second", 45],
  ["minutes", "minute", 45],
  ["hours", "hour", 22],
  ["days", "day", 26],
  ["months", "month", 11],
];

const intlRelativeTime = (
  target: Date,
  lang: string,
  now: number,
): string | null => {
  let formatter: Intl.RelativeTimeFormat;
  try {
    if (Intl.RelativeTimeFormat.supportedLocalesOf([lang]).length === 0) {
      return null;
    }
    formatter = new Intl.RelativeTimeFormat(lang, { numeric: "always" });
  } catch {
    return null;
  }

  const diff = target.getTime() - now;
  const sign = diff < 0 ? -1 : 1;
  const duration = moment.duration(Math.abs(diff));
  for (const [momentUnit, intlUnit, limit] of INTL_STEPS) {
    const value = Math.round(duration.as(momentUnit));
    if (value < limit) {
      return formatter.format(sign * Math.max(1, value), intlUnit);
    }
  }
  return formatter.format(
    sign * Math.max(1, Math.round(duration.as("years"))),
    "year",
  );
};

/** `target` relative to now, e.g. "in 4 Tagen" for German. */
export const formatRelativeTime = (
  target: moment.MomentInput,
  lang?: string,
  now: number = Date.now(),
): string => {
  const date = moment(target).toDate();
  const locale = momentLocaleFor(lang);
  if (locale) {
    return moment(date).locale(locale).from(now);
  }
  return (
    intlRelativeTime(date, normalize(lang), now) ??
    moment(date).locale("en").from(now)
  );
};
